import React, { useState } from "react";
import { useRouter } from "next/router";
import { API_ENDPOINT } from "../../utils/Constants";
import styles from "./BookMeetingModal.module.scss";

const TIME_SLOTS_IS = [
    { value: "morning", label: "Morgun (9:00–12:00)" },
    { value: "afternoon", label: "Eftir hádegi (12:00–15:00)" },
    { value: "late_afternoon", label: "Síðdegi (15:00–17:00)" },
];

const TIME_SLOTS_EN = [
    { value: "morning", label: "Morning (9:00–12:00)" },
    { value: "afternoon", label: "Afternoon (12:00–15:00)" },
    { value: "late_afternoon", label: "Late afternoon (15:00–17:00)" },
];

const DAY_OPTIONS_IS = [
    { value: "asap", label: "Eins fljótt og hægt er" },
    { value: "this_week", label: "Á þessari viku" },
    { value: "next_week", label: "Á næstu viku" },
    { value: "flexible", label: "Sveigjanlegt" },
];

const DAY_OPTIONS_EN = [
    { value: "asap", label: "As soon as possible" },
    { value: "this_week", label: "This week" },
    { value: "next_week", label: "Next week" },
    { value: "flexible", label: "Flexible" },
];

export default function BookMeetingModal({
    contactName = "",
    contactEmail = "",
    contactCompany = "",
    recommendation = "",
    compositeScores = null,
    quizScore = 0,
    onClose,
}) {
    const { locale } = useRouter();
    const isEn = locale === "en";

    const [name, setName] = useState(contactName);
    const [email, setEmail] = useState(contactEmail);
    const [phone, setPhone] = useState("");
    const [timeSlot, setTimeSlot] = useState("");
    const [dayPref, setDayPref] = useState("asap");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const timeSlots = isEn ? TIME_SLOTS_EN : TIME_SLOTS_IS;
    const dayOptions = isEn ? DAY_OPTIONS_EN : DAY_OPTIONS_IS;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !email || !phone) return;
        setError("");
        setSending(true);

        try {
            const res = await fetch(`${API_ENDPOINT}public/book-meeting`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name,
                    email,
                    phone,
                    company: contactCompany,
                    time_slot: timeSlot,
                    day_preference: dayPref,
                    message,
                    recommendation,
                    quiz_score: quizScore,
                    composite_scores: compositeScores,
                }),
            });

            if (res.ok) {
                setSent(true);
            } else {
                const data = await res.json().catch(() => ({}));
                setError(
                    data.message ||
                        (isEn
                            ? "Something went wrong. Please try again."
                            : "Eitthvað fór úrskeiðis. Reyndu aftur."),
                );
            }
        } catch {
            setError(
                isEn
                    ? "Connection error. Please try again."
                    : "Tengingarvilla. Reyndu aftur.",
            );
        } finally {
            setSending(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                <button
                    className={styles.closeBtn}
                    onClick={onClose}
                    aria-label="Close"
                >
                    ×
                </button>

                {sent ? (
                    <div className={styles.successContainer}>
                        <div className={styles.successIcon}>✓</div>
                        <h3 className={styles.successTitle}>
                            {isEn ? "Thank you!" : "Takk fyrir!"}
                        </h3>
                        <p className={styles.successText}>
                            {isEn
                                ? `We'll be in touch shortly, ${name.split(" ")[0]}. A confirmation has been sent to ${email}.`
                                : `Við munum hafa samband fljótlega, ${name.split(" ")[0]}. Staðfesting hefur verið send á ${email}.`}
                        </p>
                    </div>
                ) : (
                    <>
                        <h2 className={styles.title}>
                            {isEn
                                ? "Book a short intro call"
                                : "Bóka stuttan kynningu-fund"}
                        </h2>
                        <p className={styles.subtitle}>
                            {isEn
                                ? "We'll give you a quick tour of the platform and answer any questions."
                                : "Við sýnum þér kerfið í stuttri kynningu og svörum spurningum."}
                        </p>

                        <form className={styles.form} onSubmit={handleSubmit}>
                            <div className={styles.row}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.label}>
                                        {isEn ? "Name" : "Nafn"} *
                                    </label>
                                    <input
                                        className={styles.input}
                                        type="text"
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        required
                                    />
                                </div>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.label}>
                                        {isEn ? "Phone" : "Sími"} *
                                    </label>
                                    <input
                                        className={styles.input}
                                        type="tel"
                                        placeholder={
                                            isEn
                                                ? "e.g. 777-1234"
                                                : "t.d. 777-1234"
                                        }
                                        value={phone}
                                        onChange={(e) =>
                                            setPhone(e.target.value)
                                        }
                                        required
                                    />
                                </div>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>
                                    {isEn ? "Email" : "Netfang"} *
                                </label>
                                <input
                                    className={styles.input}
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className={styles.row}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.label}>
                                        {isEn
                                            ? "Preferred day"
                                            : "Óskaður dagur"}
                                    </label>
                                    <select
                                        className={styles.select}
                                        value={dayPref}
                                        onChange={(e) =>
                                            setDayPref(e.target.value)
                                        }
                                    >
                                        {dayOptions.map((o) => (
                                            <option
                                                key={o.value}
                                                value={o.value}
                                            >
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.label}>
                                        {isEn
                                            ? "Preferred time"
                                            : "Óskaður tími"}
                                    </label>
                                    <select
                                        className={styles.select}
                                        value={timeSlot}
                                        onChange={(e) =>
                                            setTimeSlot(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            {isEn
                                                ? "Any time"
                                                : "Hvenær sem er"}
                                        </option>
                                        {timeSlots.map((s) => (
                                            <option
                                                key={s.value}
                                                value={s.value}
                                            >
                                                {s.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>
                                    {isEn
                                        ? "Message (optional)"
                                        : "Skilaboð (valfrjálst)"}
                                </label>
                                <textarea
                                    className={styles.textarea}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder={
                                        isEn
                                            ? "Anything specific you'd like to discuss?"
                                            : "Eitthvað sérstakt sem þú vilt ræða?"
                                    }
                                    rows={2}
                                />
                            </div>

                            {error && (
                                <p className={styles.errorText}>{error}</p>
                            )}

                            <button
                                type="submit"
                                className={styles.submitBtn}
                                disabled={sending || !name || !email || !phone}
                            >
                                {sending
                                    ? isEn
                                        ? "Sending..."
                                        : "Sendi..."
                                    : isEn
                                      ? "Send request"
                                      : "Senda beiðni"}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
